import { z } from 'zod';

/**
 * Configurazione del validator Zod centralizzato
 */
interface ZodValidatorConfig {
	/** Abilita/disabilita la validazione (default: solo in development) */
	enabled?: boolean;
	/** Prefisso per i log di errore */
	logPrefix?: string;
	/** Funzione custom per logging degli errori */
	onValidationError?: (error: z.ZodError, rawData: any, context: string) => void;
}

/**
 * Validator Zod centralizzato per runtime validation
 * Attivo solo in development per default, configurabile
 */
class ZodValidator {
	private config: Required<ZodValidatorConfig>;

	constructor(config: ZodValidatorConfig = {}) {
		this.config = {
			enabled: config.enabled ?? process.env.NODE_ENV === 'development',
			logPrefix: config.logPrefix ?? '[Zod Validation]',
			onValidationError: config.onValidationError ?? this.defaultErrorHandler.bind(this),
		};
	}

	/**
	 * Handler di default per gli errori di validazione
	 */
	private defaultErrorHandler(error: z.ZodError, rawData: any, context: string): void {
		console.group(`${this.config.logPrefix} ${context}`);
		console.error('❌ Validation failed:');
		console.error('📋 Formatted errors:', error.format());
		console.error('📦 Raw data:', rawData);
		console.error('📍 Issues:', error.issues);
		console.groupEnd();
	}

	/**
	 * Valida un singolo oggetto con lo schema fornito
	 * @param data - Dati da validare
	 * @param schema - Schema Zod per la validazione
	 * @param context - Contesto per i log di errore (es: "Holiday.getById")
	 * @returns I dati originali (non modificati)
	 */
	validateSingle<T>(data: unknown, schema: z.ZodSchema<T>, context: string): unknown {
		if (!this.config.enabled) {
			return data;
		}

		const result = schema.safeParse(data);
		if (!result.success) {
			this.config.onValidationError(result.error, data, context);
		}

		return data; // Restituisce sempre i dati originali
	}

	/**
	 * Valida un array di oggetti con lo schema fornito
	 * @param dataArray - Array di dati da validare
	 * @param schema - Schema Zod per ogni elemento
	 * @param context - Contesto per i log di errore (es: "Holiday.getAll")
	 * @returns L'array originale (non modificato)
	 */
	validateArray<T>(dataArray: unknown[], schema: z.ZodSchema<T>, context: string): unknown[] {
		if (!this.config.enabled) {
			return dataArray;
		}

		dataArray.forEach((item, index) => {
			const result = schema.safeParse(item);
			if (!result.success) {
				this.config.onValidationError(result.error, item, `${context}[${index}]`);
			}
		});

		return dataArray; // Restituisce sempre i dati originali
	}

	/**
	 * Valida una response OData con array
	 * @param response - Response OData con proprietà value
	 * @param schema - Schema Zod per ogni elemento nell'array
	 * @param context - Contesto per i log di errore
	 * @returns La response originale (non modificata)
	 */
	validateODataResponse<T>(
		response: { value: unknown[] },
		schema: z.ZodSchema<T>,
		context: string,
	): { value: unknown[] } {
		if (!this.config.enabled) {
			return response;
		}

		this.validateArray(response.value, schema, `${context}.value`);
		return response; // Restituisce sempre la response originale
	}

	/**
	 * Abilita o disabilita la validazione runtime
	 * @param enabled - true per abilitare, false per disabilitare
	 */
	setEnabled(enabled: boolean): void {
		this.config.enabled = enabled;
	}

	/**
	 * Verifica se la validazione è attualmente abilitata
	 */
	isEnabled(): boolean {
		return this.config.enabled;
	}
}

/**
 * Istanza singleton del validator Zod
 * Configurato per essere attivo solo in development
 */
export const zodValidator = new ZodValidator();

/**
 * Esporta la classe per creare istanze personalizzate se necessario
 */
export { ZodValidator };
export type { ZodValidatorConfig };
